import * as THREE from 'three';
import { PICK_MATERIAL } from '../core/materials.js';
import { VoxelGrid } from '../core/voxel-grid.js';
import { createVoxelMaterial } from '../core/materials.js';
import { mulberry32 } from '../core/rng.js';
import { WIND } from '../config.js';

// ---------------------------------------------------------------------------
// A Zaanse stellingmolen ("stage mill"): tapered octagonal body clad in
// thatch, a wooden gallery ringing it at first-floor height so the miller can
// reach the sails from above the rooftops, and a cap that turns to face the
// wind. Built as three separate meshes because the cap turns, the sails spin,
// and the body does neither.
// ---------------------------------------------------------------------------

const BODY_W = 27;      // body grid footprint (voxels)
const BODY_H = 30;
const CENTER = 13;      // body grid centre column
const STAGE_Y = 12;     // gallery height
const CAP_Y = 22;       // where the body stops and the cap begins
const SAIL_R = 17;      // sail radius in voxels

export function registerMillBlocks(palette) {
    return {
        brick: palette.add('millBrick', 0x936256, { jitter: 0.09 }),
        brickDark: palette.add('millBrickDark', 0x7a5049, { jitter: 0.08 }),
        thatch: palette.add('millThatch', 0x9c8055, { jitter: 0.1 }),
        thatchDark: palette.add('millThatchDark', 0x7d6742, { jitter: 0.09 }),
        board: palette.add('millBoard', 0x4d6350, { jitter: 0.07 }),
        white: palette.add('millWhite', 0xe7e3d6, { jitter: 0.035 }),
        timber: palette.add('millTimber', 0x876b43, { jitter: 0.08 }),
        timberDark: palette.add('millTimberDark', 0x5e4c2e, { jitter: 0.07 }),
        canvas: palette.add('millCanvas', 0xd9d2bd, { jitter: 0.05 }),
        window: palette.add('millWindow', 0xffcf8a, { emissive: 1.0, jitter: 0.02 }),
    };
}

// --- body -------------------------------------------------------------------

/**
 * An eight-sided course. A smock mill is octagonal, and a cylinder is not —
 * the flats and the corners are the whole look of one.
 */
function octagon(g, cx, cz, y, r, id) {
    const cut = r * 1.32;
    for (let dz = -Math.ceil(r); dz <= Math.ceil(r); dz++) {
        for (let dx = -Math.ceil(r); dx <= Math.ceil(r); dx++) {
            if (Math.abs(dx) > r || Math.abs(dz) > r) continue;
            if (Math.abs(dx) + Math.abs(dz) > cut) continue;      // corners off
            g.set(cx + dx, y, cz + dz, id);
        }
    }
}

function buildBody(P, accentId, { smock = false } = {}) {
    const g = new VoxelGrid(BODY_W, BODY_H, BODY_W);

    if (smock) {
        // De Kat is a paint mill standing on its own storehouse: in 1960 the
        // octagonal mill was lifted off another site and set down on top of
        // the old De Kat warehouse. So it is a square brick building with an
        // eight-sided wooden mill on the roof, which is why it looks like no
        // other mill on the river.
        // Wider than the gallery above it, or the square base hides inside
        // the round stage and the whole point is lost.
        const half = 11;
        g.box(CENTER - half, 0, CENTER - half, CENTER + half, 5, CENTER + half, P.brick);
        g.box(CENTER - half, 6, CENTER - half, CENTER + half, 6, CENTER + half, P.brickDark);
        // Loading doors on two faces, and windows between them.
        g.box(CENTER - 2, 1, CENTER - half, CENTER + 2, 4, CENTER - half, P.timber);
        g.box(CENTER - 2, 1, CENTER + half, CENTER + 2, 4, CENTER + half, P.timber);

        // The smock itself: eight sides, tapering, boarded rather than thatched.
        for (let y = 7; y < CAP_Y - 1; y++) {
            const t = (y - 7) / (CAP_Y - 8);
            octagon(g, CENTER, CENTER, y, 7.4 * (1 - 0.42 * t), P.thatch);
        }
        // Every few courses a darker band, the way tarred boarding weathers.
        for (let y = 9; y < CAP_Y - 2; y += 4) {
            const t = (y - 7) / (CAP_Y - 8);
            octagon(g, CENTER, CENTER, y, 7.4 * (1 - 0.42 * t), P.thatchDark);
        }
    } else {
        // Brick undercroft: the mill stands on a squat masonry drum.
        g.cylinder(CENTER, CENTER, 0, 4, 8.6, P.brick);
        g.ring(CENTER, CENTER, 4, 8.9, P.brickDark, { thickness: 1.2 });

        // Tapered thatched body.
        g.cylinder(CENTER, CENTER, 5, CAP_Y - 1, 7.9, P.thatch, { taper: 0.44 });

        // Horizontal banding — thatch is laid in courses and the seams show.
        for (let y = 7; y < CAP_Y - 1; y += 4) {
            const t = (y - 5) / (CAP_Y - 6);
            g.ring(CENTER, CENTER, y, 7.9 * (1 - 0.44 * t) + 0.15, P.thatchDark, { thickness: 1.0 });
        }
    }

    // The gallery: planked deck on brackets, with a railing.
    g.ring(CENTER, CENTER, STAGE_Y, 10.4, P.timber, { thickness: 3.2 });
    g.ring(CENTER, CENTER, STAGE_Y + 1, 10.4, P.timber, { thickness: 0.9 });
    g.ring(CENTER, CENTER, STAGE_Y + 2, 10.4, P.white, { thickness: 0.9 });
    // Brackets under the deck so it does not read as floating.
    for (let a = 0; a < 16; a++) {
        const ang = (a / 16) * Math.PI * 2;
        const bx = Math.round(CENTER + Math.cos(ang) * 8.6);
        const bz = Math.round(CENTER + Math.sin(ang) * 8.6);
        g.set(bx, STAGE_Y - 1, bz, P.timberDark);
        g.set(bx, STAGE_Y - 2, bz, P.timberDark);
    }

    // Stair up to the gallery.
    for (let i = 0; i < 10; i++) {
        const x = CENTER + 8 + Math.floor(i * 0.35);
        g.set(x, 3 + i, CENTER + 4, P.timber);
        g.set(x, 3 + i, CENTER + 5, P.timber);
    }

    // Door and shuttered windows, in the mill's accent colour.
    g.box(CENTER - 1, 1, CENTER + 8, CENTER + 1, 3, CENTER + 8, P.timberDark);
    g.set(CENTER - 2, 2, CENTER + 8, accentId);
    g.set(CENTER + 2, 2, CENTER + 8, accentId);
    for (const [ox, oz] of [[0, 7], [7, 0], [0, -7], [-7, 0]]) {
        const wx = CENTER + Math.round(ox * 0.92);
        const wz = CENTER + Math.round(oz * 0.92);
        g.set(wx, 8, wz, P.window);
        g.set(wx, 9, wz, P.white);
        g.set(wx, 16, wz, P.window);
    }

    // White collar where the cap sits.
    g.ring(CENTER, CENTER, CAP_Y - 1, 4.9, P.white, { thickness: 1.4 });

    return g;
}

// --- cap --------------------------------------------------------------------

const CAP_W = 13;
const CAP_C = 6;

function buildCap(P, accentId) {
    const g = new VoxelGrid(CAP_W, 10, CAP_W);

    // Onion-ish white cap: stacked discs shrinking toward the top.
    const profile = [4.6, 4.7, 4.6, 4.2, 3.6, 2.8, 1.8];
    for (let i = 0; i < profile.length; i++) {
        g.cylinder(CAP_C, CAP_C, i, i, profile[i], i === 0 ? P.white : P.white);
    }
    // Accent band around the base of the cap.
    g.ring(CAP_C, CAP_C, 1, 4.8, accentId, { thickness: 1.0 });
    g.set(CAP_C, 7, CAP_C, P.timberDark);

    // The tail pole (staart) the miller pushes to turn the cap into the wind.
    for (let i = 1; i <= 8; i++) {
        g.set(CAP_C, Math.max(0, 4 - Math.floor(i * 0.45)), CAP_C - i, P.timber);
    }
    g.set(CAP_C, 0, CAP_C - 8, P.timberDark);

    // Windshaft stub poking out the front, where the sails bolt on.
    g.box(CAP_C, 4, CAP_C + 3, CAP_C, 4, CAP_C + 5, P.timberDark);

    return g;
}

// --- sails ------------------------------------------------------------------

const SAIL_W = SAIL_R * 2 + 3;
const SAIL_C = SAIL_R + 1;

function buildSails(P, rng) {
    const g = new VoxelGrid(SAIL_W, SAIL_W, 3);

    // Hub.
    g.box(SAIL_C - 1, SAIL_C - 1, 0, SAIL_C + 1, SAIL_C + 1, 2, P.timberDark);

    // Four arms. A mill's sail frame is asymmetric: the lattice and canvas sit
    // on one side of the stock, which is why turning sails read as blades
    // rather than as a spinning plus sign.
    for (let k = 0; k < 4; k++) {
        const ang = (k * Math.PI) / 2;
        const dx = Math.round(Math.cos(ang));
        const dy = Math.round(Math.sin(ang));
        const px = -dy;   // perpendicular, the side the canvas hangs on
        const py = dx;

        for (let r = 2; r <= SAIL_R; r++) {
            const bx = SAIL_C + dx * r;
            const by = SAIL_C + dy * r;
            // Stock (the main beam).
            g.set(bx, by, 1, P.timber);
            if (r < SAIL_R - 1) g.set(bx, by, 0, P.timberDark);

            const reach = r < 4 ? 0 : Math.min(5, Math.round((r - 3) * 0.55) + 2);
            for (let s = 1; s <= reach; s++) {
                const lx = bx + px * s;
                const ly = by + py * s;
                // Lattice ribs every other voxel...
                if (r % 2 === 0) g.set(lx, ly, 1, P.timber);
                // ...with canvas stretched over the inner two-thirds.
                if (s <= reach - 1 && r > 5 && r < SAIL_R - 1 && rng() > 0.06) {
                    g.set(lx, ly, 2, P.canvas);
                }
            }
        }
    }
    return g;
}

// ---------------------------------------------------------------------------

/**
 * Build one windmill.
 *
 * @param {object}  ctx      { env, palette, voxelSize, blocks }
 * @param {object}  cfg      an entry from MILLS in config.js
 */
export function createWindmill(ctx, cfg) {
    const { env, palette, voxelSize } = ctx;
    const P = ctx.millBlocks;
    const rng = mulberry32(cfg.id.length * 7919 + 13);

    const accentId = palette.add(`accent_${cfg.id}`, cfg.accent, { jitter: 0.04 });

    const material = createVoxelMaterial(env, { highlight: 0xffe2ad });

    const group = new THREE.Group();
    group.name = `mill:${cfg.id}`;

    // --- body ---
    const bodyGeo = buildBody(P, accentId, { smock: cfg.smock }).build(palette, {
        voxelSize,
        origin: new THREE.Vector3(-CENTER * voxelSize, 0, -CENTER * voxelSize),
    });
    const body = new THREE.Mesh(bodyGeo, material);
    group.add(body);

    // --- cap (turns about Y) ---
    const capGroup = new THREE.Group();
    capGroup.position.y = CAP_Y * voxelSize;
    const capGeo = buildCap(P, accentId).build(palette, {
        voxelSize,
        origin: new THREE.Vector3(-CAP_C * voxelSize, 0, -CAP_C * voxelSize),
    });
    capGroup.add(new THREE.Mesh(capGeo, material));
    group.add(capGroup);

    // --- sails (spin about Z, carried by the cap) ---
    const sailGroup = new THREE.Group();
    sailGroup.position.set(0, 4 * voxelSize, 6.2 * voxelSize);
    const sailGeo = buildSails(P, rng).build(palette, {
        voxelSize,
        origin: new THREE.Vector3(-SAIL_C * voxelSize, -SAIL_C * voxelSize, 0),
    });
    sailGroup.add(new THREE.Mesh(sailGeo, material));
    capGroup.add(sailGroup);

    capGroup.rotation.y = cfg.facing;

    // --- invisible pick volume ---
    // Raycasting against thin lattice sails is a coin flip, so hovering is
    // tested against a cylinder that wraps the whole mill.
    const pickHeight = (CAP_Y + 8) * voxelSize;
    const pick = new THREE.Mesh(
        new THREE.CylinderGeometry(SAIL_R * voxelSize * 0.85, 9 * voxelSize, pickHeight, 12),
        PICK_MATERIAL,
    );
    pick.position.y = pickHeight / 2;
    pick.renderOrder = -1;
    // A mill is something to read about now, not something to click through
    // to a website — the links live in the village houses.
    pick.userData.landmark = cfg.card;
    pick.userData.anchor = new THREE.Vector3(0, (CAP_Y + 2) * voxelSize, 0);
    group.add(pick);

    // Soft contact shadow, since the voxel shader has no shadow map.
    const shadow = new THREE.Mesh(
        new THREE.CircleGeometry(10.5 * voxelSize, 24),
        new THREE.MeshBasicMaterial({
            color: 0x1a2417,
            transparent: true,
            opacity: 0.3,
            depthWrite: false,
        }),
    );
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.y = 0.06;
    group.add(shadow);

    let spin = cfg.spin;
    let highlight = 0;

    return {
        config: cfg,
        group,
        pick,
        material,
        /** World-space point the HUD label should hang from. */
        labelAnchor: new THREE.Vector3(0, (CAP_Y + 6) * voxelSize, 0),

        update(dt, { hovered = false, focused = false } = {}) {
            // The miller lets the sails out when someone is watching.
            const target = cfg.spin * (hovered || focused ? 3.1 : 1);
            spin += (target - spin) * Math.min(1, dt * 2.2);
            sailGroup.rotation.z -= spin * dt;

            // The cap follows the wind — that is what a cap is for, and with
            // the weather backing and veering it makes the mills the one thing
            // on the river that tells you where it has got to.
            const wobble = Math.sin(env.uniforms.uTime.value * 0.11 + cfg.bank * 9) * 0.09;
            const want = WIND.direction + Math.PI / 2 + wobble;
            let turn = want - capGroup.rotation.y;
            // Take the short way round, or it unwinds the long way at the seam.
            turn = Math.atan2(Math.sin(turn), Math.cos(turn));
            capGroup.rotation.y += turn * Math.min(1, dt * 0.5);

            const wantHighlight = hovered || focused ? 0.16 : 0;
            highlight += (wantHighlight - highlight) * Math.min(1, dt * 8);
            material.uniforms.uHighlightAmount.value = highlight;
            shadow.material.opacity = 0.16 + 0.2 * Math.max(0, env.uniforms.uSunElevation.value);
        },
    };
}

export { CENTER as MILL_CENTER, BODY_W as MILL_FOOTPRINT };
